/**
 * @file    useFieldsFilter.ts
 * @scope   VIEW
 * @summary Client-side search/filtering of the field list: builds a searchable haystack per field
 *          and decides when the search box replaces the "Expand fields" toggle.
 * @consumes SelectedFieldModel
 * @produces Visible-field list (with original indices), search term state + handlers
 * @sideEffects None — local state only; notifies the caller to collapse cards on search change.
 * @notes WHY keep the original index: every downstream handler/ref addresses fields by their index
 *        in selectedUserFields.Fields, so the filtered view must carry it alongside the field.
 */
import { useCallback, useMemo, useState } from 'react';
import { SelectedFieldModel } from 'models/Enterprise/FieldsManagerModel';

/** Number of fields from which the search box replaces the "Expand fields" toggle. */
export const FIELDS_SEARCH_THRESHOLD = 10;

export interface VisibleField {
  field: SelectedFieldModel;
  /** Original index within selectedUserFields.Fields — required by every handler/ref. */
  index: number;
}

interface UseFieldsFilterParams {
  fields: SelectedFieldModel[];
  /** Called on every search term change (typing or clearing) to collapse every card. */
  onSearchTermChange?: () => void;
}

/** Builds the lowercase haystack of a field from every searchable text property. */
const getSearchableText = (field: SelectedFieldModel): string => {
  const rawField = (field.field ?? {}) as Record<string, unknown>;
  const candidates = [field.displayName, rawField.displayName, rawField.longText];

  return candidates
    .filter((value): value is string => typeof value === 'string')
    .join(' ')
    .toLowerCase();
};

/**
 * Case-insensitive substring match: every whitespace-separated token of the term
 * must appear anywhere inside the field text (not only at the beginning).
 * e.g. "bet" and "gamma alfa" both match "alfa beta gamma".
 */
const matchesTerm = (field: SelectedFieldModel, tokens: string[]): boolean => {
  const haystack = getSearchableText(field);
  return tokens.every((token) => haystack.includes(token));
};

/**
 * Owns the FieldsManager header search state and derives the visible fields list.
 * Keeps the original index of every field so handlers, refs and reorder keep working.
 */
export const useFieldsFilter = ({ fields, onSearchTermChange }: UseFieldsFilterParams) => {
  const [searchTerm, setSearchTerm] = useState('');

  const isSearchMode = fields.length >= FIELDS_SEARCH_THRESHOLD;

  const handleSearchTermChange = useCallback(
    (value: string) => {
      setSearchTerm(value);
      onSearchTermChange?.();
    },
    [onSearchTermChange]
  );

  const visibleFields = useMemo<VisibleField[]>(() => {
    const indexed = fields.map((field, index) => ({ field, index }));
    const tokens = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
    if (!isSearchMode || tokens.length === 0) return indexed;
    return indexed.filter(({ field }) => matchesTerm(field, tokens));
  }, [fields, searchTerm, isSearchMode]);

  return {
    searchTerm,
    isSearchMode,
    visibleFields,
    handleSearchTermChange,
  };
};
